import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
import { toast } from "sonner";
import { X, Save, Eraser, Info } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * PassportRedactor — draw black redaction boxes on a PDF preview
 * so sensitive fields (PAN, bank details, addresses) never reach the investor.
 *
 * Two supported modes:
 *  1. `mode="investor"` (default) — redacts an investor Passport PDF, using
 *     the auto-generated preview at `/admin/investors/{id}/passport-preview`.
 *  2. `mode="document"` — redacts any founder-uploaded PDF, using
 *     `/admin/documents/{doc_id}/view`. Redaction boxes are persisted per doc.
 *
 * Implementation strategy:
 *  - Renders the passport PDF inside an <object> for reliable native preview.
 *  - Overlays a transparent canvas the reviewer drags to draw boxes.
 *  - Coordinates are stored as page-percentages so they survive future PDF
 *    regenerations without pixel drift.
 */
export default function PassportRedactor({ investorId, docId, mode = "investor", title, onClose }) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const isDocMode = mode === "document" && docId;
  const redactionsUrl = isDocMode
    ? `/admin/documents/${docId}/redactions`
    : `/admin/investors/${investorId}/passport-redactions`;
  const previewUrl = isDocMode
    ? `${base}/api/admin/documents/${docId}/view`
    : `${base}/api/admin/investors/${investorId}/passport-preview`;
  const [cacheKey, setCacheKey] = useState(() => Date.now());
  const [boxes, setBoxes] = useState([]); // {x_pct, y_pct, w_pct, h_pct, page}
  const [presets, setPresets] = useState([]);
  const [draft, setDraft] = useState(null); // in-progress rect during drag
  const [saving, setSaving] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDims, setPageDims] = useState({ width: 800, height: 1120 });
  const overlayRef = useRef(null);
  const startRef = useRef(null);

  // react-pdf needs a stable file object to avoid re-fetching on every render.
  const [fileObj, setFileObj] = useState(() => ({ url: `${previewUrl}?v=${Date.now()}`, withCredentials: true }));
  useEffect(() => {
    setFileObj({ url: `${previewUrl}?v=${cacheKey}`, withCredentials: true });
  }, [previewUrl, cacheKey]);

  useEffect(() => {
    api.get(redactionsUrl)
      .then((r) => setBoxes(r.data?.redactions || []))
      .catch(() => setBoxes([]));
    // Passport-style presets only make sense for the auto-generated investor
    // passport template. For arbitrary founder documents, coords don't align.
    if (!isDocMode) {
      api.get("/admin/investors/passport-redaction-presets")
        .then((r) => setPresets(r.data?.presets || []))
        .catch(() => setPresets([]));
    }
  }, [redactionsUrl, isDocMode]);

  const applyPreset = (preset) => {
    // Avoid duplicates — if every box from the preset already exists
    // (by coord match) treat it as a toggle-off instead.
    const boxKey = (b) => `${b.page}-${b.x_pct.toFixed(2)}-${b.y_pct.toFixed(2)}-${b.w_pct.toFixed(2)}-${b.h_pct.toFixed(2)}`;
    const existing = new Set(boxes.map(boxKey));
    const alreadyAll = preset.boxes.every((b) => existing.has(boxKey(b)));
    if (alreadyAll) {
      // Toggle off
      const removeKeys = new Set(preset.boxes.map(boxKey));
      setBoxes(boxes.filter((b) => !removeKeys.has(boxKey(b))));
      toast.info(`Removed "${preset.label}" preset.`);
    } else {
      const merged = [...boxes];
      preset.boxes.forEach((b) => {
        if (!existing.has(boxKey(b))) merged.push(b);
      });
      setBoxes(merged);
      toast.success(`Applied "${preset.label}".`);
    }
  };

  const onMouseDown = useCallback((e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    startRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDraft({ x: startRef.current.x, y: startRef.current.y, w: 0, h: 0 });
  }, []);
  const onMouseMove = useCallback((e) => {
    if (!startRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setDraft({
      x: Math.min(startRef.current.x, x),
      y: Math.min(startRef.current.y, y),
      w: Math.abs(x - startRef.current.x),
      h: Math.abs(y - startRef.current.y),
    });
  }, []);
  const onMouseUp = useCallback(() => {
    if (!draft || !overlayRef.current) { startRef.current = null; return; }
    const rect = overlayRef.current.getBoundingClientRect();
    // Only commit if the box is at least 6×6 pixels (avoids stray clicks).
    if (draft.w > 6 && draft.h > 6) {
      const box = {
        page: currentPage,
        x_pct: (draft.x / rect.width) * 100,
        y_pct: (draft.y / rect.height) * 100,
        w_pct: (draft.w / rect.width) * 100,
        h_pct: (draft.h / rect.height) * 100,
      };
      setBoxes((prev) => [...prev, box]);
    }
    setDraft(null);
    startRef.current = null;
  }, [draft]);

  const removeBox = (idx) => setBoxes((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => setBoxes([]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(redactionsUrl, { redactions: boxes });
      toast.success(`Saved ${boxes.length} redaction${boxes.length === 1 ? "" : "s"}.`);
      setCacheKey(Date.now());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="passport-redactor">
      <div className="w-full max-w-5xl h-[90vh] bg-white rounded-lg shadow-2xl border border-navy/10 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-navy/10 px-4 py-3 bg-white">
          <div className="flex items-center gap-2">
            <div className="font-heading text-ink text-sm">{title || "Passport Preview · Redactor"}</div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              {boxes.length} redaction{boxes.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearAll}
              disabled={boxes.length === 0 || saving}
              className="text-xs px-3 py-1.5 rounded border border-navy/15 text-ink-soft hover:bg-navy/[0.04] font-heading inline-flex items-center gap-1 disabled:opacity-40"
              data-testid="redactor-clear"
            >
              <Eraser size={12} /> Clear
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-heading inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="redactor-save"
            >
              <Save size={12} /> {saving ? "Saving…" : "Save & preview"}
            </button>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink p-1"
              data-testid="redactor-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="px-4 py-2 border-b border-navy/10 bg-emerald-50/40 flex items-center gap-2 text-xs text-emerald-900">
          <Info size={12} className="flex-shrink-0" />
          <span>
            Drag anywhere on the passport to draw a black redaction. Click a saved box to remove it.
            Boxes are stored as percentages so they survive future passport regenerations.
          </span>
        </div>
        {presets.length > 0 && (
          <div className="px-4 py-2 border-b border-navy/10 bg-white flex items-center gap-2 flex-wrap" data-testid="redactor-presets">
            <span className="overline mr-1">Quick-redact:</span>
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-navy/15 text-ink-soft hover:border-emerald-400 hover:text-emerald-800 hover:bg-emerald-50 font-heading transition-colors"
                title={p.hint}
                data-testid={`redactor-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
        {numPages > 1 && (
          <div className="px-4 py-2 border-b border-navy/10 bg-white flex items-center gap-2 text-xs" data-testid="redactor-page-nav">
            <span className="text-ink-muted">Page</span>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border border-navy/15 disabled:opacity-30"
              data-testid="redactor-page-prev"
            >‹</button>
            <span className="font-mono">{currentPage} / {numPages}</span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
              disabled={currentPage === numPages}
              className="px-2 py-1 rounded border border-navy/15 disabled:opacity-30"
              data-testid="redactor-page-next"
            >›</button>
          </div>
        )}
        <div className="flex-1 relative overflow-auto bg-navy/[0.04] flex justify-center py-4">
          <div className="relative" style={{ width: pageDims.width, height: pageDims.height }}
            data-testid="redactor-pdf-doc">
            <Document
              file={fileObj}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => toast.error(`PDF load failed: ${e?.message || e}`)}
              loading={<div className="p-6 text-sm text-ink-muted">Loading document…</div>}
              error={<div className="p-6 text-sm text-rose-700">Failed to load PDF.</div>}
            >
              <Page
                pageNumber={currentPage}
                width={800}
                onLoadSuccess={(page) => setPageDims({ width: page.width, height: page.height })}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                data-testid={`redactor-pdf-page-${currentPage}`}
              />
            </Document>
            <div
              ref={overlayRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              className="absolute inset-0 cursor-crosshair"
              data-testid="redactor-overlay"
            >
              {boxes.filter((b) => (b.page || 1) === currentPage).map((b, i) => (
                <div
                  key={`${b.page}-${i}-${b.x_pct}-${b.y_pct}`}
                  onClick={(e) => { e.stopPropagation(); removeBox(boxes.indexOf(b)); }}
                  className="absolute bg-black/90 hover:bg-rose-700 cursor-not-allowed transition-colors"
                  title="Click to remove"
                  style={{
                    left: `${b.x_pct}%`, top: `${b.y_pct}%`,
                    width: `${b.w_pct}%`, height: `${b.h_pct}%`,
                  }}
                  data-testid={`redaction-box-${boxes.indexOf(b)}`}
                />
              ))}
              {draft && (
                <div
                  className="absolute bg-black/50 border border-gold"
                  style={{ left: draft.x, top: draft.y, width: draft.w, height: draft.h }}
                  data-testid="redaction-draft"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
